import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { FunctionService } from "../../model-services/function.service";
import { OrganisationService } from "../../organisation/organisation.service";
import { SCREEN } from "../layouts-enums";
import { Organisation } from "../../common/organisation";
import { AppComponent } from "../../app.component";
import { DownloadService } from "../../model-services/download.service";
import { UserService } from "../../user/user.service";
import { OnboardingService } from "../../user/onboarding.service";
import { Router } from "@angular/router";
import { GET_STARTED_URL } from "../../app-urls";
import { OrganisationTransferService } from "../../organisation/organisation-transfer.service";

@Component({
    selector: 'app-organisations-layout',
    templateUrl: './organisations-layout.html',
    styleUrl: './organisations-layout.css',
    standalone: false
})
export class OrganisationsLayoutComponent implements OnInit, OnDestroy {
    screen:SCREEN = SCREEN.ORGANISATIONS

    helpGuides:string[] = []        // the help guides about this screen
    organisations:Organisation[] = []
    allOrganisations:Organisation[] = []
    loading:boolean
    loaded = false

    // system admins see every organisation, so they get extra columns, filters and exports
    isSysAdmin = false
    private updatedSub:Subscription

    possibleValues = ["Any", "Yes", "No"]

    isPartner:string = this.possibleValues[0]
    isCustomer:string = this.possibleValues[0]
    consumption:string = this.possibleValues[0]

    constructor(private functionService: FunctionService, private organisationService:OrganisationService, public app:AppComponent,
        private downloadService:DownloadService, private userService:UserService, private onboardingService:OnboardingService, private router:Router,
        private transferService:OrganisationTransferService) {

            this.helpGuides = ['getting-started', 'linq-overview', 'organisations-and-users']
    }

    ngOnInit(): void {
        this.isSysAdmin = this.userService.isSysAdmin()

        this.organisationService.organisation = null
        // closing or editing an organisation from the list reports through the service
        this.updatedSub = this.organisationService.updatedOrganisationEvent.subscribe(() => this.loadOrganisations(false))
        this.loadOrganisations(true)
    }

    ngOnDestroy(): void {
        this.updatedSub?.unsubscribe()
    }

    handleFunctionEvent(event) {
        this.functionService.executeFunction(event.function, event.value)
    }

    async loadOrganisations(firstLoad:boolean = false) {
        this.loading = true
        // someone sent on to the ways to start isn't shown the list on the way
        let sentOn = false
        try {
            const response = await this.organisationService.getOrganisations()
            this.allOrganisations = response?.organisations || []
            this.filterChange()
            if (firstLoad) sentOn = this.onboardingService.organisationsListed(this.allOrganisations)
        }finally {
            if (!sentOn) {
                this.loading = false
                this.loaded = true
            }
        }
    }

    // a new organisation is set up with its first model
    newOrganisation() {
        this.router.navigateByUrl(GET_STARTED_URL())
    }

    async exportAdministrators() {
        const admins = await this.organisationService.getAdministrators()
        this.downloadService.createCSVAdminDownload(admins)
    }

    // system admins only: a new organisation from an export file, opened once it is made
    async importOrganisation() {
        const key = await this.transferService.importAsNewOrganisation(busy => this.loading = busy)
        if (key) this.router.navigateByUrl(`organisations/${key}`)
    }

    isFiltered() {
        return this.isPartner !== this.possibleValues[0] || this.isCustomer !== this.possibleValues[0] || this.consumption !== this.possibleValues[0]
    }

    clearFilters() {
        this.isPartner = this.isCustomer = this.consumption = this.possibleValues[0]
        this.filterChange()
    }

    filterChange() {
        this.organisations = this.allOrganisations.filter(o => {
            if (this.isCustomer === this.possibleValues[1] && o.notCustomer) return false
            if (this.isCustomer === this.possibleValues[2] && !o.notCustomer) return false
            if (this.isPartner === this.possibleValues[1] && !o.isPartner) return false
            if (this.isPartner === this.possibleValues[2] && o.isPartner) return false
            if (this.consumption === this.possibleValues[1] && o.isExemptFromPayment) return false
            if (this.consumption === this.possibleValues[2] && !o.isExemptFromPayment) return false
            return true
        })
    }
}
